import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { InvoiceStatus, RetailPaymentErrorCode } from '@galaxy/billing';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

declare let Stripe; // : stripe.StripeStatic;

@Component({
  selector: 'smb-invoicing-billing-payment-form',
  templateUrl: './payment-form.component.html',
  styleUrls: ['./payment-form.component.scss'],
  standalone: false,
})
export class PaymentFormComponent implements AfterViewInit, OnDestroy {
  @ViewChild('paymentElement') paymentElement: ElementRef;
  @Input() clientSecret: string;
  @Input() stripePublicKey: string;
  @Input() merchantId: string;
  @Input() invoiceId: string;
  @Input() stripeAccount: string;
  @Input() primaryColor: string;
  @Input() invoiceStatus: string;
  @Input() lastErrorCode: RetailPaymentErrorCode;
  @Input() isPreview: boolean;
  @Output() processingPaymentEvent: EventEmitter<void> = new EventEmitter();

  private _isPaying$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly _isPaying$: Observable<boolean> = this._isPaying$$.asObservable();

  stripe;
  elements;
  payment;
  immediateError: string;
  lastPaymentProcessingError: string;
  paymentHandler = this.onChange.bind(this);

  constructor(
    private changeDetector: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {}

  get isPaying$(): Observable<boolean> {
    return this._isPaying$;
  }

  ngAfterViewInit(): void {
    this.stripe = Stripe(this.stripePublicKey, {
      stripeAccount: this.stripeAccount,
    });
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
    });
    this.payment = this.elements.create('payment', {
      disabled: this.isPaymentButtonDisabled(),
      type: 'tabs',
      defaultCollapsed: false,
    });
    this.payment.mount(this.paymentElement.nativeElement);
    this.payment.addEventListener('change', this.paymentHandler);

    if (this.lastErrorCode) {
      if (this.lastErrorCode === RetailPaymentErrorCode.AccountClosed) {
        this.lastPaymentProcessingError = this.translateService.instant(
          'PAYMENT_FORM.LATEST_PROCESSING_ERROR.NO_ACCOUNT',
        );
      } else if (this.lastErrorCode === RetailPaymentErrorCode.InsufficientFunds) {
        this.lastPaymentProcessingError = this.translateService.instant(
          'PAYMENT_FORM.LATEST_PROCESSING_ERROR.INSUFFICIENT_FUNDS',
        );
      } else if (this.lastErrorCode === RetailPaymentErrorCode.DebitNotAuthorized) {
        this.lastPaymentProcessingError = this.translateService.instant(
          'PAYMENT_FORM.LATEST_PROCESSING_ERROR.DEBIT_NOT_AUTHORIZED',
        );
      } else {
        this.lastPaymentProcessingError = this.translateService.instant('PAYMENT_FORM.LATEST_PROCESSING_ERROR.UNKNOWN');
      }
    }
  }

  ngOnDestroy(): void {
    this.payment.removeEventListener('change', this.paymentHandler);
    this.payment.destroy();
  }

  onChange({ error }): void {
    if (error) {
      this.immediateError = error.message;
    } else {
      this.immediateError = '';
    }
    this.changeDetector.detectChanges();
  }

  isPaymentButtonDisabled(): boolean {
    return this.invoiceStatus === InvoiceStatus.DRAFT || this.isPreview;
  }

  async onSubmit(): Promise<void> {
    this._isPaying$$.next(true);
    await this.stripe
      .confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: `${window.location.href}`,
        },
        redirect: 'if_required',
      })
      .then((result) => {
        if (result.error) {
          this._isPaying$$.next(false);
          this.immediateError = result.error.message;
        } else {
          this.processingPaymentEvent.emit(null);
        }
      });
  }
}
